import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ConversazioneBreve } from '@masamune/shared';
import { Icon } from '../../components/ui/Icon';
import { MEmpty, MGroup, MRow } from '../../components/mobile/MobileKit';
import { ApiError } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { conversazioniQuery, eliminaConversazione } from './chatApi';
import { quandoDiRoma } from './registroApi';
import { PAROLE_CONVERSAZIONI as P, confermaEliminaConversazione } from './riquadriTesto';
import './riquadri.css';

/**
 * «Conversazioni» nel pannello di MUNE (Fase 2, fetta 2B «Chiedi», 29/10/2026;
 * os-planning/ai/fase-2-progetto.md, «Interfaccia»): le conversazioni toccate negli ultimi 30
 * giorni (GET /ai/conversazioni, al massimo 50, le più recenti prima). Il job notturno
 * cancella quelle più vecchie, così «Restano 30 giorni» è vero dal primo giorno.
 *
 * Un clic la apre nella chat e si continua da lì: `onApri`, il pannello la carica (e prima
 * interrompe un turno che sta scrivendo). Al computer, al passaggio, il cestino: conferma e
 * via, poi `onEliminata` (se era quella aperta, il pannello ne comincia una nuova). Al
 * telefono niente cestino sulle righe: si elimina dal menu ⋯ della conversazione aperta.
 *
 * Al computer titolo e «Restano 30 giorni» li scrive l'intestazione del pannello, come
 * «Cosa sa MUNE» per la memoria; al telefono stanno nel gruppo, come nelle altre pagine.
 */
export function ConversazioniVista({ attuale, mobile, onApri, onEliminata }: {
  attuale: string | null;
  mobile: boolean;
  onApri: (id: string) => void;
  /** Dopo l'eliminazione (conferma e chiamata le fa la vista): se era quella aperta, il pannello ne comincia una nuova. */
  onEliminata: (id: string) => void;
}): JSX.Element {
  const qc = useQueryClient();
  const q = useQuery(conversazioniQuery);
  const lista: ConversazioneBreve[] = q.data ?? [];
  // Se non riesce solo un ricaricamento, resta l'elenco che c'era: l'errore solo senza dati.
  const errore = q.isError && !q.data;

  const elimina = useMutation({
    mutationFn: async (id: string): Promise<boolean> => {
      if (!(await confermaEliminaConversazione())) return false;
      try {
        await eliminaConversazione(id);
      } catch (e) {
        // Già sparita (la pulizia della notte, o eliminata da un'altra scheda): per chi la
        // voleva togliere è fatto lo stesso.
        if (!(e instanceof ApiError && e.status === 404)) throw e;
      }
      return true;
    },
    onSuccess: (fatto, id) => {
      if (!fatto) return;
      qc.setQueryData<ConversazioneBreve[]>(conversazioniQuery.queryKey, (v) => v?.filter((c) => c.id !== id));
      void qc.invalidateQueries({ queryKey: conversazioniQuery.queryKey });
      onEliminata(id);
    },
    // La frase dell'API è già in italiano; la rete che cade darebbe «Failed to fetch».
    onError: (e: unknown) => toast.alert('Conversazione non eliminata', e instanceof ApiError ? e.message : 'Controlla la connessione e riprova'),
  });

  const sotto = (c: ConversazioneBreve) => `${quandoDiRoma(c.aggiornata)}${c.id === attuale ? ` · ${P.aperta}` : ''}`;
  const titolo = (c: ConversazioneBreve) => (c.titolo ?? '').trim() || P.senzaTitolo;

  if (mobile) {
    return (
      <MGroup title={P.titolo} count={lista.length || undefined} note={P.restano}>
        {q.isLoading ? (
          <MEmpty>{P.carico}</MEmpty>
        ) : errore ? (
          <MEmpty>{P.errore}</MEmpty>
        ) : !lista.length ? (
          <MEmpty>{P.vuota}</MEmpty>
        ) : lista.map((c) => (
          <MRow key={c.id} icon="message" title={titolo(c)} sub={sotto(c)} onClick={() => onApri(c.id)} />
        ))}
      </MGroup>
    );
  }

  if (q.isLoading) return <p className="cv-stato">{P.carico}</p>;
  if (errore) return <p className="cv-stato">{P.errore}</p>;
  if (!lista.length) return <p className="cv-stato">{P.vuota}</p>;

  return (
    <ul className="cv" aria-label="Conversazioni degli ultimi 30 giorni">
      {lista.map((c) => {
        const questa = elimina.isPending && elimina.variables === c.id;
        return (
          <li key={c.id} className="cv-riga" aria-current={c.id === attuale ? 'true' : undefined}>
            {/* Il titolo intero al passaggio: nella riga sta su una riga sola. */}
            <button type="button" className="cv-apri" title={titolo(c)} onClick={() => onApri(c.id)}>
              <span className="cv-titolo">{titolo(c)}</span>
              <span className="cv-quando">{sotto(c)}</span>
            </button>
            <button
              type="button"
              className="cv-elimina"
              title="Elimina questa conversazione"
              aria-label={`Elimina la conversazione «${titolo(c)}»`}
              data-occupato={questa ? 'true' : undefined}
              disabled={elimina.isPending}
              onClick={() => elimina.mutate(c.id)}
            >
              <Icon name="trash" size={13} />
            </button>
          </li>
        );
      })}
    </ul>
  );
}
